/**
 * The notifications client (PR 8) — the one header bell and Settings' System
 * notifications tab. Backend: backend/api/notifications_api.py.
 *
 *   GET  /api/notifications                    the bell: For you / Data / System
 *   POST /api/notifications/read               mark items read for me
 *   POST /api/notifications/read-all           mark my whole bell read
 *   GET  /api/notifications/preferences        what reaches my bell / my email
 *   PUT  /api/notifications/preferences        change them
 *   GET  /api/notifications/incidents          admin: one row per incident
 *   POST /api/notifications/incidents/resolve  admin: mark one / a source resolved
 *
 * Read state and preferences are per person (the server keys them on the
 * signed-in identity). Every mutation carries X-Requested-With, the CSRF
 * guard the backend checks. Nothing here reaches Amazon; "Mark resolved"
 * hides an incident until it happens again, it does not fix anything.
 */
import { req, reqMut } from './api'
import { isApiError } from './query/apiError'

/** The alert table's severity words (backend/observability/alerts.py). */
export type NotificationSeverity = 'info' | 'warning' | 'error' | 'critical'

export type NotificationItem = {
  /** Stable per item: 'incident:sync:definitions:sd', 'approvals', 'alert-def:12', 'data:ads'. */
  key: string
  severity: NotificationSeverity
  title: string
  detail: string
  /** Where the fix lives; null when there is nothing to open. */
  href: string | null
  /** 'open_pending_tray' opens the header tray instead of navigating. */
  action: 'open_pending_tray' | null
  count: number
  first_seen_at: string | null
  last_seen_at: string | null
  changed_at: string | null
  unread: boolean
}

export type NotificationSectionId = 'for_you' | 'data' | 'system'

export type NotificationSection = {
  id: NotificationSectionId
  label: string
  /** The section's own empty copy ("Nothing is waiting on you."). */
  empty: string
  /** Items past the 20 shown. */
  more: number
  items: NotificationItem[]
}

export type NotificationFeed = {
  generated_at: string
  viewer: { is_admin: boolean }
  /** Unread warning/error/critical items — info items never raise the badge. */
  unread: number
  worst_unread: NotificationSeverity | null
  sections: NotificationSection[]
}

/** The five things a person can route (backend/notifications/prefs.py CATEGORIES). */
export type NotificationCategory = 'approvals' | 'pending_changes' | 'business_alerts' | 'data' | 'system'

export type NotificationPrefs = {
  bell: Record<NotificationCategory, boolean>
  email: Record<NotificationCategory, boolean>
  /** False until email is set up on the server AND the digest is switched on. */
  email_available: boolean
  email_note: string | null
}

export type IncidentStatus = 'open' | 'resolved' | 'all'

export type Incident = {
  fingerprint: string
  source: string
  title: string
  detail: string
  severity: NotificationSeverity
  first_seen_at: string | null
  last_seen_at: string | null
  occurrence_count: number
  rows: number
  resolved_at: string | null
  /** recovered (fixed itself) | manual (someone marked it) | superseded | disabled | not_enrolled
   *  | lapsed (stopped happening for long enough that its next failure is a new problem) */
  resolved_reason: string | null
  /** The producer no longer alerts (perf, listing digest): legacy rows only. */
  retired: boolean
  latest_message: string | null
  latest_context: unknown
}

export type IncidentQuery = {
  status?: IncidentStatus
  source?: string | null
  include_retired?: boolean
  limit?: number
}

const BASE = '/api/notifications'

function incidentsQs(q: IncidentQuery): string {
  const p = new URLSearchParams()
  if (q.status) p.set('status', q.status)
  if (q.source) p.set('source', q.source)
  if (q.include_retired) p.set('include_retired', 'true')
  if (q.limit != null) p.set('limit', String(q.limit))
  const s = p.toString()
  return s ? `?${s}` : ''
}

export const notificationsApi = {
  feed: () => req<NotificationFeed>(BASE),
  markRead: (keys: string[]) => reqMut<{ marked: number }>(`${BASE}/read`, { keys }),
  markAllRead: () => reqMut<{ marked: number }>(`${BASE}/read-all`, {}),
  getPrefs: () => req<NotificationPrefs>(`${BASE}/preferences`),
  savePrefs: (prefs: Pick<NotificationPrefs, 'bell' | 'email'>) =>
    req<NotificationPrefs>(`${BASE}/preferences`, {
      method: 'PUT',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ bell: prefs.bell, email: prefs.email }),
    }),
  listIncidents: (q: IncidentQuery = {}) =>
    req<{ incidents: Incident[] }>(`${BASE}/incidents${incidentsQs(q)}`).then((r) => r.incidents),
  /** One incident (`fingerprint`) or every open incident of a `source`. */
  resolveIncident: (target: { fingerprint: string } | { source: string }) =>
    reqMut<{ resolved: number }>(`${BASE}/incidents/resolve`, target),
}

/** What the System notifications tab says when the incidents list fails:
 *  plain English, never the raw "403 Forbidden: {json}" — a non-admin who
 *  reaches the tab is told it is for admins. */
export function incidentsErrorText(e: unknown): string {
  if (isApiError(e) && e.status === 403) return 'Only admins can see system problems.'
  if (isApiError(e)) return e.serverMessage ?? `The list could not load (error ${e.status}). Try again in a minute.`
  return 'The list could not load. Try again in a minute.'
}
